import Sentry from './sentry';
import { checkCanOpenPDFVersion } from './version';

export const queryStringToObj = (str) => {
  const res = {};
  if (!str) return res;
  const params = str.split('&');
  params.forEach((param) => {
    if (!param) return;
    const pArray = param.split('=');
    res[pArray[0]] = pArray[1];
  });
  return res;
};

export const getNativeMethod = (url) => {
  if (url.includes('native:')) {
    url = url.replace('native:', '');
  }
  const splitArr = url.split('?');
  return {
    method: splitArr[0],
    params: queryStringToObj(splitArr[1]),
  };
};

// 判断平台
export const getPlatform = (ua = navigator.userAgent) => {
  const uaLower = ua.toLowerCase();
  if (/(iphone|ipad|ipod|ios)/i.test(uaLower)) {
    return 'ios';
  } else if (uaLower.indexOf('android') > -1 || uaLower.indexOf('linux') > -1) {
    return 'android';
  } else {
    return 'others';
  }
};

export const getAppVersion = () => {
  const ua = navigator.userAgent.toLowerCase();
  const platform = getPlatform();
  if (platform === 'ios' || platform === 'android') {
    // eslint-disable-next-line
    const versions = ua.match(/appver\/([\d\.]+)/);
    const version = versions && versions[1];
    return version || '0';
  }
  return '0';
};

export const getOSInfo = (ua = navigator.userAgent) => {
  //自定义头
  //os/Android_7.0
  //os/ios_14.5
  let reg = /os\/(\S+)_(\d+(\.\d+)?)/;
  let matched = ua.match(reg);
  if (matched) {
    return {
      platform: (matched[1] || '').toLocaleLowerCase(),
      version: matched[2],
    };
  }
  //无自定义头
  reg = /Android (\d+(?:\.\d+)*)/i;
  matched = ua.match(reg);
  if (matched) {
    return {
      platform: 'android',
      version: matched[1],
    };
  }

  if (getPlatform(ua) === 'ios') {
    reg = /OS ((\d+_?){2,3})\s/;
    matched = ua.match(reg);
    if (matched) {
      return {
        platform: 'ios',
        version: (matched[1] || '').replace(/_/g, '.'),
      };
    }
  }
  //手动上报sentry
  const err = new Error('Illegal UA');
  err.name = 'IllegalUAError';
  Sentry.captureException(err);
  return {
    platform: '',
    version: '',
  };
};

// deepMerge
interface IObject {
  [k: string]: any;
}
type MergeType = Array<any> | IObject;
const emptyTarget = (val: MergeType) => (Array.isArray(val) ? [] : {});
const getType = (a: any) => Object.prototype.toString.call(a).slice(8, -1);

const isMergeableObject = (a: MergeType) => {
  const cls = getType(a);
  return cls === 'Array' || cls === 'Object';
};

const clone = (value: MergeType): MergeType =>
  isMergeableObject(value) ? deepmerge(emptyTarget(value), value) : value;

const mergeArray = (target: Array<any>, source: Array<any>) =>
  target.concat(source).map((element) => clone(element));

const mergeObject = (target: IObject, source: IObject) => {
  const dest: IObject = {};
  if (isMergeableObject(target)) {
    Object.keys(target).forEach((key) => {
      dest[key] = clone(target[key]);
    });
  }
  Object.keys(source).forEach((key) => {
    if (!isMergeableObject(source[key]) || !target[key])
      dest[key] = clone(source[key]);
    else dest[key] = deepmerge(target[key], source[key]);
  });
  return dest;
};

export const deepmerge = <T extends MergeType, P extends MergeType>(
  target: T,
  source: P,
): T & P => {
  const sourceType = getType(source);
  const targetType = getType(target);
  if (sourceType !== targetType) return clone(source) as T & P;
  else {
    if (sourceType === 'Array')
      return mergeArray(target as Array<any>, source as Array<any>) as T & P;
    else return mergeObject(target, source) as T & P;
  }
};

// 获取app版本字符串形式，格式 e.g. 2.16.0
export const getAppVersionString = (): string => {
  const ua = navigator.userAgent.toLowerCase();
  const platform = getPlatform();
  if (platform === 'ios' || platform === 'android') {
    // eslint-disable-next-line
    const versions = ua.match(/appver\/([\d\.]+)/);
    const version = versions && versions[1];
    return version || '';
  }
  return '';
};

// name display: Camel Case
export const getCamelCaseString = (value: string) => {
  if (typeof value !== 'string') return value;
  const valuesArr = value.toLowerCase().split(' ');
  return valuesArr
    .map((item) => (item?.[0]?.toUpperCase() ?? '') + item?.slice(1))
    .join(' ');
};

export const pdfView = (pdfPath: string) => {
  if (checkCanOpenPDFVersion()) {
    let url = pdfPath;
    // pdf upload in docs
    if (/help-center\/assets\/pdf/.test(pdfPath)) {
      url = `${window.location.origin}${pdfPath}`;
    }
    (window as MyWindow).WebViewJavascriptBridge.callHandler(
      'openExternalLink',
      {
        url,
      },
    );
  }
};
